/**
 * Homepage hero carousel — the DEFAULT homepage hero (A/B ab_hero_home retired 2026-07-02).
 *
 * Bespoke vanilla carousel (no library), following the W3C ARIA APG carousel
 * pattern. Each slide background is an image or a Cloudflare-hosted MP4. Autoplay
 * with a per-slide duration: image slides use [data-duration] seconds (fallback
 * 4s), video slides use the real video length. A drift-free requestAnimationFrame
 * clock drives the progress ring and auto-advance.
 *
 * Scope: runs whenever a [data-hero-carousel] element is on the page (the homepage
 * renders it only when hero slides are set).
 *
 * Full-video popup reuses the global components/data-video-popup.js: the "Watch
 * video" button carries data-video-iframe. We do NOT edit that file — we detect
 * its open (capture-phase click on our own button) and close (MutationObserver on
 * the .rd-video-popup node) to pause/resume the background video + autoplay.
 */
(function () {
  'use strict'

  var MOBILE_MAX = 767 // px — below this use the mobile background video source
  var IMAGE_FALLBACK_MS = 4000 // image slide duration when none set in the CMS
  var VIDEO_FALLBACK_MS = 8000 // used until a video's real duration is known / if it errors
  var SWIPE_THRESHOLD = 40 // px

  function perfNow() {
    return window.performance && performance.now ? performance.now() : Date.now()
  }

  function createCarousel(root) {
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-hero-slide]'))
    if (!slides.length) return

    var live = root.querySelector('[data-hero-live]')
    var prevBtn = root.querySelector('[data-hero-prev]')
    var nextBtn = root.querySelector('[data-hero-next]')
    var playBtn = root.querySelector('[data-hero-playpause]')
    var progressBar = root.querySelector('[data-hero-progress]')
    var counterCurrent = root.querySelector('[data-hero-counter-current]')

    var reduceMq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null
    var reduceMotion = reduceMq ? reduceMq.matches : false

    // ── State ──────────────────────────────────────────────
    var index = 0
    var playing = false // user intent to auto-rotate
    var popupOpen = false
    var pageHidden = !!document.hidden
    var running = false // is the clock currently accumulating?
    var slideStart = 0 // perf timestamp the current run segment began
    var elapsed = 0 // ms banked before the current segment (preserved across pauses)
    var currentDurationMs = IMAGE_FALLBACK_MS
    var advancedForEntry = false // guard: advance once per slide entry
    var lastAdvancing = null // last play/pause button state, to flip the icon only on change
    var lastBreakpoint = currentBreakpoint()
    var rafId = null
    var touchX = null
    var touchY = null

    // pick the server-rendered active slide as the start index
    slides.forEach(function (s, j) {
      if (s.classList.contains('is-active')) index = j
    })

    // ── Helpers ────────────────────────────────────────────
    function currentBreakpoint() {
      return window.innerWidth <= MOBILE_MAX ? 'mobile' : 'desktop'
    }

    function clockRunning() {
      return playing && !popupOpen && !pageHidden && !reduceMotion && slides.length > 1
    }

    function setProgress(p) {
      if (progressBar) progressBar.style.strokeDashoffset = String(100 - Math.max(0, Math.min(1, p)) * 100)
    }

    function videoOf(i) {
      return slides[i] ? slides[i]._heroVideo : null
    }

    function isVideoSlide(i) {
      return !!(slides[i] && slides[i]._heroIsVideo)
    }

    // Whether the CURRENT slide is genuinely progressing right now. The play/pause
    // button reflects THIS (real playback), not the autoplay intent — so a blocked
    // or paused background video shows the "play" (stopped) icon, never a misleading
    // "pause" icon while the progress ring sits still.
    function isAdvancing() {
      var slide = slides[index]
      if (slide && slide._heroIsVideo && !slide._heroFailed) {
        var v = slide._heroVideo
        return !!(v && !v.paused && !v.ended)
      }
      return running
    }

    function setVideoSource(i) {
      var v = videoOf(i)
      if (!v) return
      var desktop = v.getAttribute('data-bg-video-desktop') || ''
      var mobile = v.getAttribute('data-bg-video-mobile') || ''
      var url = currentBreakpoint() === 'mobile' && mobile ? mobile : desktop
      if (!url) return
      if (v.getAttribute('src') !== url) {
        v.muted = true
        v.playsInline = true
        v.setAttribute('src', url)
        try {
          v.load()
        } catch (e) {}
      }
    }

    // The <video muted autoplay playsinline> attribute is what actually starts the
    // background video (no user gesture needed); this explicit play() is a backstop
    // for resume / re-entry. muted/playsinline set as IDL properties for Safari.
    function playVideo(v) {
      if (!v) return
      v.muted = true
      v.playsInline = true
      try {
        var p = v.play()
        if (p && p.catch) p.catch(function () {})
      } catch (e) {}
    }

    function pauseVideo(v) {
      if (!v) return
      try {
        v.pause()
      } catch (e) {}
    }

    // Reconcile the running flag + background video with current state.
    function applyState() {
      var run = clockRunning()
      if (run && !running) {
        slideStart = perfNow() // resume: rebase so banked elapsed continues, no jump
        running = true
      } else if (!run && running) {
        elapsed += perfNow() - slideStart // pause: bank elapsed
        running = false
      }
      var mediaActive = !popupOpen && !pageHidden && !reduceMotion && (playing || slides.length === 1)
      var v = videoOf(index)
      if (v) {
        if (mediaActive) playVideo(v)
        else pauseVideo(v)
      }
    }

    function resolveImageDuration(i) {
      var sec = parseFloat(slides[i].getAttribute('data-duration'))
      return isFinite(sec) && sec > 0 ? sec * 1000 : IMAGE_FALLBACK_MS
    }

    function updateCounter() {
      if (counterCurrent) counterCurrent.textContent = String(index + 1)
    }

    function announceCurrent() {
      // only audible while aria-live is "polite" (i.e. paused)
      if (live) live.textContent = index + 1 + ' of ' + slides.length
    }

    function updatePlayButton() {
      if (!playBtn) return
      var adv = isAdvancing()
      playBtn.classList.toggle('is-paused', !adv)
      playBtn.setAttribute('aria-label', adv ? 'Pause automatic slide rotation' : 'Start automatic slide rotation')
    }

    // ── Slide transition ───────────────────────────────────
    function enterSlide(i) {
      var prevIndex = index
      index = i

      slides.forEach(function (s, j) {
        var active = j === index
        s.classList.toggle('is-active', active)
        s.setAttribute('aria-hidden', active ? 'false' : 'true')
        // inert on inactive slides: keeps their CTA/watch out of the tab order
        // (all slides are stacked in the DOM). Without this, Tab cycles through
        // every slide's controls and never leaves the hero — a keyboard trap.
        s.inert = !active
      })

      // reset the slide we left
      if (prevIndex !== index) {
        var pv = videoOf(prevIndex)
        if (pv) {
          pauseVideo(pv)
          try {
            pv.currentTime = 0
          } catch (e) {}
        }
      }

      // reset clock for the new slide
      elapsed = 0
      slideStart = perfNow()
      advancedForEntry = false
      setProgress(0)

      if (isVideoSlide(index)) {
        currentDurationMs = VIDEO_FALLBACK_MS
        setVideoSource(index)
        var v = videoOf(index)
        if (v) {
          if (isFinite(v.duration) && v.duration > 0) currentDurationMs = v.duration * 1000
          try {
            v.currentTime = 0
          } catch (e) {}
        }
      } else {
        currentDurationMs = resolveImageDuration(index)
      }

      updateCounter()
      announceCurrent()
      running = false // force applyState to re-evaluate
      // enterSlide already reset the new slide's video to currentTime 0; applyState
      // then plays it ONLY when autoplay is running. So a manual nav while paused
      // resets the video to frame 0 but leaves it paused (no playback).
      applyState()
    }

    function advance() {
      enterSlide((index + 1) % slides.length)
    }

    function goTo(i) {
      var n = slides.length
      enterSlide(((i % n) + n) % n)
    }

    function setPlaying(next) {
      playing = next
      if (live) live.setAttribute('aria-live', playing ? 'off' : 'polite')
      applyState() // kicks/pauses the bg video first…
      updatePlayButton() // …so the icon reflects the resulting real playback state
    }

    // Toggle from what's ACTUALLY happening, not the stored intent. Otherwise a
    // blocked video (button already showing "play") would take two clicks to start:
    // the first would just flip intent off.
    function togglePlay() {
      if (isAdvancing()) setPlaying(false)
      else setPlaying(true)
    }

    // Swipe navigation STOPS auto-rotation — a swipe is a deliberate "I'll take over"
    // gesture. The arrow buttons and keyboard arrows do NOT stop it: they just navigate
    // (plain goTo, which restarts the new slide's timer) and autoplay keeps running.
    // Only the pause button and a swipe turn autoplay off. Target slide stays paused
    // until play is pressed again.
    function userGoTo(i) {
      setPlaying(false)
      goTo(i)
    }

    // ── Clock ──────────────────────────────────────────────
    // VIDEO slide: the video itself is the clock — the progress ring follows its REAL
    // playback (video.currentTime / duration) and it advances on the video's 'ended'
    // event. So if the video is paused or blocked, the ring freezes and the slide does
    // NOT auto-advance — rotation and video stay in sync (no desync).
    // IMAGE slide (or a failed video): a drift-free rAF timer (perf.now deltas).
    function tick(now) {
      var slide = slides[index]
      var bg = slide && slide._heroIsVideo && !slide._heroFailed ? slide._heroVideo : null
      if (bg) {
        var dur = isFinite(bg.duration) && bg.duration > 0 ? bg.duration : 0
        if (dur > 0) setProgress(bg.currentTime / dur)
      } else if (running) {
        var total = elapsed + (now - slideStart)
        var p = currentDurationMs > 0 ? total / currentDurationMs : 0
        setProgress(p)
        if (p >= 1 && !advancedForEntry) {
          advancedForEntry = true
          advance()
        }
      }
      // Keep the play/pause button honest: poll the real advancing-state every frame
      // and flip the icon only when it actually changes (cheap; rAF runs anyway). This
      // catches Safari aborting play() async, buffering, end-of-video, etc.
      var adv = isAdvancing()
      if (adv !== lastAdvancing) {
        lastAdvancing = adv
        updatePlayButton()
      }
      rafId = window.requestAnimationFrame(tick)
    }

    // ── Per-video listeners (duration + end + error) ───────
    function wireVideo(slide, i) {
      var v = slide.querySelector('[data-hero-bg-video]')
      slide._heroVideo = v
      slide._heroIsVideo = slide.getAttribute('data-media') === 'video' && !!v
      if (!v) return
      if (slides.length === 1) v.loop = true // a lone video slide just loops

      v.addEventListener('loadedmetadata', function () {
        if (i === index && slide._heroIsVideo && isFinite(v.duration) && v.duration > 0) {
          currentDurationMs = v.duration * 1000
          // rebase so the progress ring reflects the real length from now
          elapsed = 0
          slideStart = perfNow()
          advancedForEntry = false
        }
      })
      v.addEventListener('ended', function () {
        if (i === index && clockRunning() && !advancedForEntry) {
          advancedForEntry = true
          advance()
        }
      })
      v.addEventListener('error', function () {
        // dead video must not stall the carousel — mark it failed so tick() falls back
        // to the rAF timer, and (re)start that timer so the slide still advances.
        slide._heroFailed = true
        if (i === index) {
          currentDurationMs = IMAGE_FALLBACK_MS
          elapsed = 0
          slideStart = perfNow()
          advancedForEntry = false
        }
      })
      // Safari can reject the first play() before the media is ready; once it can
      // play, re-apply state so the active slide's background video starts.
      v.addEventListener('canplay', function () {
        if (i === index) applyState()
      })
    }

    // ── Popup coordination (no edit to data-video-popup.js) + video-view analytics ──
    // `trigger` = the [data-hero-watch] button that opened the popup (gives provider/title).
    // Pushes GA4-style events to window.dataLayer: hero_video_open/close always; for a
    // Cloudflare <video> also start / progress (25·50·75) / complete / heartbeat + watched
    // seconds. YouTube is a bare iframe (no timeupdate) → open/close only, no time.
    function pauseForPopup(trigger) {
      if (popupOpen) return
      popupOpen = true
      applyState() // banks the clock + pauses bg video
      if (live) live.setAttribute('aria-live', 'polite')

      // analytics context for this open→close session
      var iframeAttr = trigger && trigger.getAttribute ? trigger.getAttribute('data-video-iframe') || '' : ''
      var provider = /youtube\.com|youtu\.be/i.test(iframeAttr) ? 'youtube' : 'cloudflare'
      var slideEl = trigger && trigger.closest ? trigger.closest('[data-hero-slide]') : slides[index]
      var sIdx = slideEl ? slides.indexOf(slideEl) : index
      var titleEl = slideEl && slideEl.querySelector('.rd-hero-carousel__title')
      var title = (titleEl && titleEl.textContent.trim()) || 'Hero video ' + (sIdx + 1)
      var slideNum = sIdx + 1
      function dl(event, extra) {
        var d = { event: event, video_provider: provider, video_title: title, video_slide: slideNum }
        if (extra) for (var k in extra) if (extra.hasOwnProperty(k)) d[k] = extra[k]
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push(d)
      }
      var maxPercent = 0
      var watched = 0 // real seconds watched (sum of forward playback deltas)
      var lastT = 0
      var hbTimer = null
      var sent = { start: false, 25: false, 50: false, 75: false, complete: false }

      dl('hero_video_open')

      // When the shared popup appears: tag it for fullscreen styling and FORCE the full
      // video to start with sound. A <video autoplay> injected via innerHTML isn't
      // reliably auto-started by browsers, so we call play() explicitly — still inside
      // the click's user activation, so unmuted playback is allowed. Resume on close.
      var handled = false
      var obs = new MutationObserver(function () {
        var popup = document.querySelector('.rd-video-popup')
        if (popup && !handled) {
          handled = true
          popup.classList.add('rd-video-popup_fullscreen')
          var vid = popup.querySelector('video')
          if (vid) {
            vid.muted = false
            var tryPlay = function () {
              var pr = vid.play()
              if (pr && pr.catch) pr.catch(function () {})
            }
            tryPlay()
            // the first play() may land before the media is ready — retry once it can play
            vid.addEventListener('canplay', tryPlay, { once: true })

            // progress tracking (Cloudflare native video only)
            vid.addEventListener('timeupdate', function () {
              var dur = vid.duration
              if (!isFinite(dur) || dur <= 0) return
              var t = vid.currentTime
              var dt = t - lastT
              if (dt > 0 && dt < 2) watched += dt // forward playback only — ignore seeks
              lastT = t
              if (!sent.start && t > 0.1) {
                sent.start = true
                dl('hero_video_start', { video_duration: Math.round(dur) })
              }
              var pct = Math.floor((t / dur) * 100)
              if (pct > maxPercent) maxPercent = pct
              ;[25, 50, 75].forEach(function (m) {
                if (!sent[m] && pct >= m) {
                  sent[m] = true
                  dl('hero_video_progress', { video_percent: m, video_current_time: Math.round(t) })
                }
              })
            })
            vid.addEventListener('ended', function () {
              if (sent.complete) return
              sent.complete = true
              maxPercent = 100
              dl('hero_video_complete', { video_percent: 100, video_current_time: Math.round(vid.duration || 0), video_duration: Math.round(vid.duration || 0) })
            })
            // heartbeat: while playing, report watched seconds every 15s
            hbTimer = window.setInterval(function () {
              if (!vid.paused && !vid.ended) {
                dl('hero_video_heartbeat', { video_current_time: Math.round(vid.currentTime), video_watched_seconds: Math.round(watched) })
              }
            }, 15000)
          }
        } else if (!popup && handled) {
          obs.disconnect()
          if (hbTimer) {
            window.clearInterval(hbTimer)
            hbTimer = null
          }
          var closeExtra = { video_percent: maxPercent }
          if (provider === 'cloudflare') closeExtra.video_watched_seconds = Math.round(watched)
          dl('hero_video_close', closeExtra)
          popupOpen = false
          applyState()
          if (live) live.setAttribute('aria-live', playing ? 'off' : 'polite')
        }
      })
      obs.observe(document.body, { childList: true })
    }

    // ── Event wiring ───────────────────────────────────────
    function bindEvents() {
      // Arrows navigate but DON'T stop autoplay (plain goTo restarts the new slide's timer)
      if (prevBtn) prevBtn.addEventListener('click', function () { goTo(index - 1) })
      if (nextBtn) nextBtn.addEventListener('click', function () { goTo(index + 1) })
      if (playBtn) playBtn.addEventListener('click', togglePlay)

      // keyboard (scoped: only when focus is within the carousel) — also keeps autoplay
      root.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault()
          goTo(index - 1)
        } else if (e.key === 'ArrowRight') {
          e.preventDefault()
          goTo(index + 1)
        }
      })

      // swipe — listen on the whole hero (root), not just the slides viewport, so the
      // bottom strip (where the controls sit, a common place to swipe on mobile) isn't a
      // dead zone. Only a clearly HORIZONTAL gesture flips slides, so vertical page
      // scrolls (and diagonal drifts) never trigger an accidental change.
      root.addEventListener(
        'touchstart',
        function (e) {
          if (!e.touches || !e.touches.length) return
          touchX = e.touches[0].clientX
          touchY = e.touches[0].clientY
        },
        { passive: true }
      )
      root.addEventListener(
        'touchend',
        function (e) {
          if (touchX === null || !e.changedTouches || !e.changedTouches.length) return
          var dx = e.changedTouches[0].clientX - touchX
          var dy = e.changedTouches[0].clientY - touchY
          touchX = null
          touchY = null
          if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
            userGoTo(dx < 0 ? index + 1 : index - 1)
          }
        },
        { passive: true }
      )
      root.addEventListener('touchcancel', function () {
        touchX = null
        touchY = null
      })

      // Watch-video click — capture phase so we run BEFORE data-video-popup's
      // bubble-phase stopPropagation(). Detect open here; close via MutationObserver.
      document.addEventListener(
        'click',
        function (e) {
          var btn = e.target && e.target.closest ? e.target.closest('[data-hero-watch]') : null
          if (btn && root.contains(btn)) pauseForPopup(btn)
        },
        true
      )

      // tab visibility — freeze on hide, resume without a jump on return
      document.addEventListener('visibilitychange', function () {
        pageHidden = !!document.hidden
        applyState()
      })

      // resize — re-measure the chrome (banner/header height can change with width) and
      // swap the video source only when the breakpoint actually crosses (no thrash)
      var resizeTimer = null
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer)
        resizeTimer = setTimeout(function () {
          measureChrome()
          var bp = currentBreakpoint()
          if (bp === lastBreakpoint) return
          lastBreakpoint = bp
          if (isVideoSlide(index)) enterSlide(index)
        }, 200)
      })

      // re-measure after late-loading banner content (countdown image / fonts) settles
      window.addEventListener('load', measureChrome)

      // react to OS reduced-motion changes
      if (reduceMq) {
        var onMq = function (e) {
          reduceMotion = e.matches
          if (reduceMotion) setPlaying(false)
          else applyState()
        }
        if (reduceMq.addEventListener) reduceMq.addEventListener('change', onMq)
        else if (reduceMq.addListener) reduceMq.addListener(onMq)
      }
    }

    // Set --hero-chrome to the distance from the top of the document to the hero — i.e.
    // everything stacked above it in flow: the announcement banner (ONLY when present)
    // plus the sticky header. The CSS min-height then fills exactly the rest of the screen
    // (100svh - chrome), so the hero height = 100% − header (− banner when it's shown).
    function measureChrome() {
      var chrome = root.getBoundingClientRect().top + (window.pageYOffset || 0)
      root.style.setProperty('--hero-chrome', Math.max(0, Math.round(chrome)) + 'px')
    }

    // ── Init ───────────────────────────────────────────────
    slides.forEach(wireVideo)
    bindEvents()
    // Set the autoplay intent BEFORE the first enterSlide, so applyState plays the
    // active video straight away instead of pause()→play() (which Safari aborts). The
    // <video autoplay> attribute is the reliable trigger; this just avoids the race.
    playing = !reduceMotion && slides.length > 1
    measureChrome()
    enterSlide(index)
    updatePlayButton()
    if (live) live.setAttribute('aria-live', playing ? 'off' : 'polite')
    rafId = window.requestAnimationFrame(tick)
  }

  function init() {
    // Carousel is the default homepage hero now (A/B retired) — run whenever it's present.
    var root = document.querySelector('[data-hero-carousel]')
    if (!root) return
    createCarousel(root)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
